// frontend/src/services/api.js
import axios from "axios";

/**
 * BACKEND BASE URL
 *
 * In local CRA dev, use "/api" so requests go through the React proxy.
 * In production, set REACT_APP_API_BASE_URL if needed.
 *
 * IMPORTANT:
 * If baseURL is "/api", then login must call "/auth/login"
 * NOT "/api/auth/login".
 */
const pickBaseUrl = () => {
  const envBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "";

  const raw = String(envBase || "").trim();

  if (!raw) {
    return "/api";
  }

  let s = raw.replace(/\/+$/, "");

  if (s === "/api" || s.toLowerCase() === "api") {
    return "/api";
  }

  s = s.replace(/\/api$/i, "");

  if (!s) {
    return "/api";
  }

  if (/^https?:\/\//i.test(s)) {
    return `${s}/api`;
  }

  return s.startsWith("/") ? s : `/${s}`;
};

const BASE_URL = pickBaseUrl();

let loggedBase = false;

const sanitizeId = (v) => {
  if (v == null) return "";

  let s = String(v).trim();

  try {
    s = decodeURIComponent(s);
  } catch (_e) {
    // ignore invalid URI encoding
  }

  s = s.replace(/^<+/, "").replace(/>+$/, "");

  return s.trim();
};

const getEffectiveSiteId = () => {
  const selectedSiteId = localStorage.getItem("selectedSiteId");

  if (selectedSiteId) {
    return sanitizeId(selectedSiteId);
  }

  try {
    const userData =
      JSON.parse(localStorage.getItem("userData") || "null") ||
      JSON.parse(localStorage.getItem("user") || "null");

    const site = userData?.site;

    if (!site) {
      return "";
    }

    return sanitizeId(typeof site === "object" ? site._id || "" : site);
  } catch (_e) {
    return "";
  }
};

const cleanParams = (params = {}) => {
  const out = {};

  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;

    out[key] = value;
  }

  return out;
};

const withNoCache = (params = {}) =>
  cleanParams({
    ...params,
    _cb: Date.now(),
  });

const _withNoCache = (params = {}) =>
  cleanParams({
    cacheBust: Date.now(),
    ...params,
  });

const getNormalizedPath = (configUrl = "") => {
  const raw = String(configUrl || "").trim();

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      return url.pathname || "/";
    } catch (_e) {
      return "/";
    }
  }

  return raw.startsWith("/") ? raw : `/${raw}`;
};

const stripApiPrefix = (pathname = "") =>
  String(pathname || "").replace(/^\/api\b/i, "") || "/";

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: false,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  if (!loggedBase) {
    console.log("[API BASE_URL]", BASE_URL);
    loggedBase = true;
  }

  const pathname = getNormalizedPath(config.url);
  const cleanPath = stripApiPrefix(pathname);

  const isPublic =
    cleanPath === "/sites/public" ||
    cleanPath.startsWith("/sites/public/") ||
    cleanPath === "/auth/login" ||
    cleanPath === "/auth/register" ||
    cleanPath === "/auth/forgot-password" ||
    cleanPath === "/auth/reset-password";

  config.headers = config.headers || {};

  if (isPublic) {
    delete config.headers.Authorization;
    return config;
  }

  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    "";

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data;
    const url = error?.config?.url;

    console.warn("[API ERROR]", status, url, data || error?.message);

    return Promise.reject(error);
  }
);

/* ========================= AUTH ========================= */

export const authAPI = {
  /**
   * Normal site/admin/director login.
   *
   * Backend final URL:
   * /api/auth/login
   *
   * Because axios baseURL is "/api", this must be "/auth/login".
   */
  login: ({ email, password, siteId }) =>
    api.post("/auth/login", {
      email,
      password,
      ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
    }),

  register: (userData) => api.post("/auth/register", userData),

  getMe: () => api.get("/auth/me"),

  forgotPassword: ({ email }) =>
    api.post("/auth/forgot-password", {
      email,
    }),

  resetPassword: ({ email, token, newPassword }) =>
    api.post("/auth/reset-password", {
      email,
      token,
      newPassword,
    }),

  changePassword: ({ currentPassword, newPassword }) =>
    api.post("/auth/change-password", {
      currentPassword,
      newPassword,
    }),
};

/* =================== PUBLIC SITES =================== */

export const sitesPublicAPI = {
  list: () => {
    console.log("[DEBUG] Fetching public sites list");

    return api
      .get("/sites/public")
      .then((response) => {
        console.log(
          "[DEBUG] Sites fetched successfully:",
          response.data?.data?.length || 0,
          "sites"
        );

        return response;
      })
      .catch((error) => {
        console.error("[DEBUG] Sites fetch failed:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          config: {
            url: error.config?.url,
            method: error.config?.method,
            headers: error.config?.headers,
          },
        });

        throw error;
      });
  },
};

/* ========================= SITES ======================== */

/* ================= OPERATIONAL SETTINGS ================= */

export const settingsAPI = {
  // What applies to this user's site. Read by the reconciliation form for the
  // float targets and the ultimate premium.
  effective: ({ siteId } = {}) =>
    api.get("/settings/effective", { params: withNoCache({ siteId }) }),

  // Admin only: every setting, what is saved globally, and which sites differ.
  all: () => api.get("/settings", { params: withNoCache({}) }),

  saveGlobal: (values) => api.put("/settings/global", { values }),

  saveSite: (siteId, values) =>
    api.put(`/settings/site/${sanitizeId(siteId)}`, { values }),

  clearSite: (siteId) => api.delete(`/settings/site/${sanitizeId(siteId)}`),
};

/* =================== REPORT DELIVERIES =================== */

/*
 * Whether the daily packs are actually arriving.
 *
 * Every attempt has been recorded since the feature shipped and none of it was
 * readable from anywhere, so the only way to find out whether a director had
 * been sent Tuesday's banking was to ask the director.
 */
export const reportDeliveriesAPI = {
  list: (params = {}) => api.get('/report-deliveries', { params: withNoCache(params) }),

  health: (days = 7) => api.get('/report-deliveries/health', { params: withNoCache({ days }) }),

  send: (siteId, businessDate) =>
    api.post('/report-deliveries/send', { siteId: sanitizeId(siteId), businessDate }),
};

export const sitesAPI = {
  getAll: () => api.get("/sites"),

  getOne: (id) => api.get(`/sites/${sanitizeId(id)}`),

  create: (data) => api.post("/sites", data),

  update: (id, data) => api.put(`/sites/${sanitizeId(id)}`, data),

  delete: (id) => api.delete(`/sites/${sanitizeId(id)}`),
};

/* ========================= USERS ======================== */

export const usersAPI = {
  create: (data) => api.post("/users", data),

  getAll: () => api.get("/users"),

  directory: () => api.get("/users", { params: withNoCache({ directory: "1" }) }),

  getOne: (id) => api.get(`/users/${sanitizeId(id)}`),

  update: (id, data) => api.put(`/users/${sanitizeId(id)}`, data),

  deactivate: (id) => api.patch(`/users/${sanitizeId(id)}/deactivate`),

  activate: (id) => api.patch(`/users/${sanitizeId(id)}/activate`),

  resetPassword: (id) => api.post(`/users/${sanitizeId(id)}/reset-password`),

  delete: (id) => api.delete(`/users/${sanitizeId(id)}`),
};

/* ===================== ADMIN USERS ===================== */

export const adminUsersAPI = {
  create: (data) => api.post("/admin/users", data),

  getAll: () => api.get("/admin/users"),

  getOne: (id) => api.get(`/admin/users/${sanitizeId(id)}`),

  update: (id, data) => api.put(`/admin/users/${sanitizeId(id)}`, data),

  deactivate: (id) => api.patch(`/admin/users/${sanitizeId(id)}/deactivate`),

  activate: (id) => api.patch(`/admin/users/${sanitizeId(id)}/activate`),

  setPassword: (id, payload) =>
    api.patch(`/admin/users/${sanitizeId(id)}/set-password`, payload),

  sendResetLink: (id) =>
    api.post(`/admin/users/${sanitizeId(id)}/send-reset-link`),
};

/* ======================= RECONCILIATION ================= */

export const reconciliationAPI = {
  getAll: (params = {}) =>
    api.get("/reconciliation", {
      params: withNoCache(params),
    }),

  // Dashboard tile counts, computed in the database. Avoids pulling every
  // reconciliation document into the browser just to count them.
  stats: (params = {}) =>
    api.get("/reconciliation/stats", {
      params: withNoCache(params),
    }),

  getOne: (id) => api.get(`/reconciliation/${sanitizeId(id)}`),

  // What this day should be compared against: the last filed day's pump
  // prices, and what a normal version of this weekday looks like here.
  dayContext: ({ date } = {}) =>
    api.get('/reconciliation/day-context', { params: withNoCache({ date }) }),

  /*
   * Everything worth raising about a day, for the site that has to fix it.
   *
   * The same buildFlags that writes the directors' email, so the dashboard and
   * the email can never disagree about what counts as a problem.
   */
  dayFlags: ({ date, siteId } = {}) =>
    api.get('/reconciliation/day-flags', { params: withNoCache({ date, siteId }) }),

  getSolarContext: ({ date, excludeId } = {}) =>
    api.get('/reconciliation/solar/context', {
      params: withNoCache({
        date,
        ...(excludeId ? { excludeId: sanitizeId(excludeId) } : {}),
      }),
    }),

  create: (data) => api.post("/reconciliation", data),

  update: (id, data) =>
    api.put(`/reconciliation/${sanitizeId(id)}`, data),

  // Which days in a range already have a reconciliation, and how far along
  // each one is. Two fields per day, so a month is cheap.
  calendar: ({ from, to, siteId } = {}) =>
    api.get("/reconciliation/calendar", {
      params: withNoCache({
        from,
        to,
        ...(siteId ? { site: sanitizeId(siteId) } : {}),
      }),
    }),

  // What a correction would change, worked out by the same code that will
  // record it — so the manager sees the list the email will carry.
  amendmentPreview: (id, data) =>
    api.post(`/reconciliation/${sanitizeId(id)}/amendment-preview`, data),

  submit: (id, body = {}) =>
    api.patch(`/reconciliation/${sanitizeId(id)}/submit`, body),

  delete: (id) =>
    api.delete(`/reconciliation/${sanitizeId(id)}`),

  resolveIssue: (id, payload = {}) =>
    api.patch(`/reconciliation/${sanitizeId(id)}/resolve-issue`, payload),

  patchResolveIssue: (id, payload = {}) =>
    api.patch(`/reconciliation/${sanitizeId(id)}/resolve-issue`, payload),

  // The backend route is /reopen-issue; /unresolve-issue does not exist and
  // this call was 404ing.
  unresolveIssue: (id, payload = {}) =>
    api.patch(`/reconciliation/${sanitizeId(id)}/reopen-issue`, payload),

  reopenIssue: (id, payload = {}) =>
    api.patch(`/reconciliation/${sanitizeId(id)}/reopen-issue`, payload),

  listSubmittedForDirector: ({ siteId, start, end, limit = 200 }) =>
    api.get("/reconciliation/director/daily", {
      params: withNoCache({
        start,
        end,
        limit,
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
      }),
    }),

  directorFeedRaw: ({ siteId, start, end, limit = 200 }) =>
    api.get("/reconciliation/director-feed", {
      params: withNoCache({
        start,
        end,
        limit,
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
      }),
    }),

  weekly: ({ siteId, start, end }) =>
    api.get("/reconciliation/weekly", {
      params: withNoCache({
        start,
        end,
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
      }),
    }),

  directorDailyList: ({ start, end, siteId, limit = 200 }) =>
    api.get("/reconciliation/director/daily", {
      params: withNoCache({
        start,
        end,
        limit,
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
      }),
    }),

  directorWeeklySummary: ({ siteId, start, end }) =>
    api.get("/reconciliation/director/weekly-summary", {
      params: withNoCache({
        start,
        end,
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
      }),
    }),

  listExceptions: ({ start, end, siteId, kind, search, history, status }) =>
    api.get("/reconciliation/exceptions", {
      params: withNoCache({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
        ...(kind ? { kind } : {}),
        ...(search ? { search } : {}),
        ...(history ? { history: "true" } : {}),
        ...(status ? { status } : {}),
      }),
    }),

  updateException: (reconciliationId, payload) =>
    api.patch(
      `/reconciliation/${sanitizeId(reconciliationId)}/exceptions`,
      payload
    ),

  createManualException: (payload) =>
    api.post("/reconciliation/exceptions/manual", payload),

  updateManualException: (exceptionId, payload) =>
    api.patch(
      `/reconciliation/exceptions/manual/${sanitizeId(exceptionId)}`,
      payload
    ),

  saveExceptionCase: (payload) =>
    api.post("/reconciliation/exceptions/case", payload),

  addExceptionRecovery: (exceptionId, payload) =>
    api.post(
      `/reconciliation/exceptions/manual/${sanitizeId(exceptionId)}/recoveries`,
      payload
    ),

  listDriveoffsNmop: (params) =>
    api.get("/reconciliation/driveoffs-nmop", {
      params: withNoCache(params),
    }),

  updateDriveoffsNmop: (reconciliationId, body) =>
    api.patch(
      `/reconciliation/${sanitizeId(reconciliationId)}/driveoffs-nmop`,
      body
    ),
};

/* ========================= UPLOADS ====================== */

export const uploadsAPI = {
  uploadDailyPaymentSummary: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/daily-payment-summary", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  uploadDailySalesException: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/daily-sales-exception", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  uploadVolumatic: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/volumatic", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  uploadLabourReport: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/labour-report", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  uploadWeeklyPaymentSummary: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/weekly-payment-summary", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  uploadWeeklySalesException: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/weekly-sales-exception", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  uploadJetwashReconciliation: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/jetwash-reconciliation", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  uploadNayaxReport: (file) => {
    const fd = new FormData();
    fd.append("file", file);

    return api.post("/uploads/nayax-report", fd, {
      timeout: 10 * 60 * 1000,
    });
  },
};

/**
 * Open an uploaded reconciliation document.
 *
 * These were plain links to /uploads/<name>, which express served publicly with
 * no login. They are now streamed behind auth, so the token has to travel in
 * the Authorization header — which a bare <a href> cannot do. Fetch as a blob
 * and open that instead, the same way the BACS module opens its documents.
 */
export const openUploadedFile = async (fileName) => {
  if (!fileName) return "";

  const response = await api.get(`/uploads/file/${encodeURIComponent(fileName)}`, {
    responseType: "blob",
  });

  const type = response.headers?.["content-type"] || "application/octet-stream";
  const url = window.URL.createObjectURL(new Blob([response.data], { type }));

  window.open(url, "_blank", "noopener");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);

  return url;
};

/* ========================== POUCH ======================= */

export const pouchAPI = {
  preview: (file, opts = {}) => {
    const form = new FormData();
    form.append("file", file);

    if (opts.siteId) {
      form.append("siteId", sanitizeId(opts.siteId));
    }

    if (opts.date) {
      form.append("date", opts.date);
    }

    if (opts.sheet) {
      form.append("sheet", opts.sheet);
    }

    return api.post("/pouch/preview", form, {
      timeout: 10 * 60 * 1000,
    });
  },

  commit: (file, opts = {}) => {
    const form = new FormData();
    form.append("file", file);

    if (opts.siteId) {
      form.append("siteId", sanitizeId(opts.siteId));
    }

    if (opts.date) {
      form.append("date", opts.date);
    }

    if (opts.sheet) {
      form.append("sheet", opts.sheet);
    }

    return api.post("/pouch/commit", form, {
      timeout: 10 * 60 * 1000,
    });
  },
};

/* ======================= DAILY SALES ==================== */

export const dailySalesAPI = {
  /*
   * `includeReconciliation` asks the server to attach what the day's
   * reconciliation holds — the pump prices and the solar reading — alongside
   * the sales.
   *
   * It was not in this signature, so every caller that passed it had it
   * silently dropped on the way out. Nobody noticed, because the server also
   * turns the same behaviour on for anyone with the director role, and the one
   * page that sent the flag is a directors' page. On Daily Sales, which site
   * managers use, the flag went nowhere and the role check was false — so the
   * fuel prices never arrived and every grade rendered as a dash.
   */
  list: ({ siteId, start, end, includeReconciliation }) =>
    api.get("/daily-sales", {
      params: withNoCache({
        start,
        end,
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
        ...(includeReconciliation ? { includeReconciliation: 1 } : {}),
      }),
    }),

  // Which days in a range are actually finished — sales filed and the
  // banking submitted — and which are only half done.
  calendar: ({ from, to, siteId } = {}) =>
    api.get("/daily-sales/calendar", {
      params: withNoCache({
        from,
        to,
        ...(siteId ? { siteId: sanitizeId(siteId) } : {}),
      }),
    }),

  preview: ({ file, siteId, date }) => {
    if (!file) {
      throw new Error("No file selected");
    }

    const fd = new FormData();
    fd.append("file", file);

    if (siteId) {
      fd.append("siteId", sanitizeId(siteId));
    }

    if (date) {
      fd.append("date", date);
    }

    return api.post("/daily-sales/debug-excel", fd, {
      params: cleanParams({
        siteId: siteId ? sanitizeId(siteId) : undefined,
        date,
      }),
      timeout: 10 * 60 * 1000,
    });
  },

  upload: ({ file, siteId, date, confirmReplace = false }) => {
    if (!file) {
      throw new Error("No file selected");
    }

    if (!siteId) {
      throw new Error("No site selected");
    }

    if (!date) {
      throw new Error("No date selected");
    }

    const fd = new FormData();
    fd.append("file", file);
    fd.append("siteId", sanitizeId(siteId));
    fd.append("date", date);
    fd.append("confirmReplace", confirmReplace ? "true" : "false");

    return api.post("/daily-sales/upload", fd, {
      params: cleanParams({
        siteId: sanitizeId(siteId),
        date,
        confirmReplace: confirmReplace ? "true" : "false",
      }),
      timeout: 10 * 60 * 1000,
    });
  },
};

/* ======================= FUEL PRICES ==================== */

export const fuelPricesAPI = {
  getLatest: (siteId) =>
    api.get("/fuel-prices/latest", {
      params: cleanParams({
        siteId: sanitizeId(siteId),
      }),
    }),

  compare: ({ siteId, date }) =>
    api.get("/fuel-prices/compare", {
      params: cleanParams({
        siteId: sanitizeId(siteId),
        date,
      }),
    }),

  upsert: ({ siteId, date, prices }) =>
    api.post("/fuel-prices/upsert", {
      siteId: sanitizeId(siteId),
      date,
      prices,
    }),

  timeline: ({ siteId, start, end, limit = 50 }) =>
    api.get("/fuel-prices/timeline", {
      params: cleanParams({
        siteId: sanitizeId(siteId),
        start,
        end,
        limit,
      }),
    }),
};

/* ========================= TANKERS ====================== */

export const tankersAPI = {
  list: ({ site, siteId, start, weekStart, end, weekEnd }) => {
    const effectiveSite = sanitizeId(site || siteId || getEffectiveSiteId());
    const effectiveStart = start || weekStart;
    const effectiveEnd = end || weekEnd;

    return api.get("/tankers/weekly", {
      params: cleanParams({
        site: effectiveSite,
        start: effectiveStart,
        end: effectiveEnd,
      }),
    });
  },

  create: (payload) => api.post("/tankers", payload),

  update: (id, payload) =>
    api.put(`/tankers/${sanitizeId(id)}`, payload),

  remove: (id) =>
    api.delete(`/tankers/${sanitizeId(id)}`),

  setOpening: (payload) => {
    const body = {
      site: sanitizeId(payload.site || payload.siteId || getEffectiveSiteId()),
      start: payload.weekStart || payload.start,
      openingStock: payload.openingStock || {},
    };

    return api.put("/tankers/weekly/opening", body);
  },

  // How full head office wants the tanks this week, and any per-grade
  // exception to that. Drives the suggested order.
  setOrderMode: (payload) => {
    const body = {
      siteId: sanitizeId(payload.site || payload.siteId || getEffectiveSiteId()),
      weekStart: payload.weekStart || payload.start,
      orderMode: payload.orderMode || "cover",
      orderModeByGrade: payload.orderModeByGrade || {},
    };

    return api.put("/tankers/weekly/order-mode", body);
  },
};

/* ========================= ALERTS ======================= */

export const alertsAPI = {
  getFuelAlerts: (params = {}) => {
    const raw = params.site || params.siteId || getEffectiveSiteId();
    const site = sanitizeId(raw);

    return api.get("/tankers/alerts", {
      params: cleanParams({
        site,
      }),
    });
  },
};

/* ==================== EQUIPMENT FAULTS ==================== */

/**
 * Open equipment faults live on their own, not inside a day's reconciliation.
 *
 * A fault is created the moment it is entered rather than when the form is
 * submitted — the equipment is broken either way, and an abandoned draft
 * should not lose it.
 */
export const equipmentFaultsAPI = {
  list: ({ siteId, status = "open", asOf } = {}) =>
    api.get("/equipment-faults", {
      params: cleanParams({
        site: sanitizeId(siteId || getEffectiveSiteId()),
        status,
        asOf,
      }),
    }),

  create: ({ siteId, description, reference, reportedTo, reportedOn } = {}) =>
    api.post("/equipment-faults", {
      site: sanitizeId(siteId || getEffectiveSiteId()),
      description,
      reference,
      reportedTo,
      reportedOn,
    }),

  markFixed: (id, { fixedOn, note } = {}) =>
    api.patch(`/equipment-faults/${sanitizeId(id)}/fixed`, { fixedOn, note }),

  markStillOpen: (id, { asOf } = {}) =>
    api.patch(`/equipment-faults/${sanitizeId(id)}/still-open`, { asOf }),
};

/* ======================= GOLDEN LINES ==================== */

const _glGetSiteId = (explicit) => {
  const siteId = sanitizeId(explicit ?? getEffectiveSiteId() ?? "");

  return siteId;
};

const _glWeekParams = (params = {}) => {
  const out = {
    ...params,
  };

  /**
   * Backend accepts:
   * - weekStart
   * OR
   * - start + end
   */
  if (out.weekStart) {
    out.weekStart = String(out.weekStart).slice(0, 10);
    delete out.start;
    delete out.end;
  } else {
    if (out.start) {
      out.start = String(out.start).slice(0, 10);
    }

    if (out.end) {
      out.end = String(out.end).slice(0, 10);
    }
  }

  return out;
};

export const goldenLinesAPI = {
  dryRunUpload: ({ file, siteId, candidateLimit = 200 }, opts = {}) => {
    if (!file) throw new Error("No file selected");
    const fd = new FormData();
    fd.append("file", file);
    const sid = _glGetSiteId(siteId);
    if (sid) fd.append("siteId", sid);
    fd.append("candidateLimit", String(candidateLimit));
    return api.post("/golden-lines/upload/dry-run", fd, {
      timeout: opts.timeout ?? 15 * 60 * 1000,
      onUploadProgress: opts.onUploadProgress,
    });
  },

  /**
   * Upload Golden Lines spreadsheet.
   *
   * Supports:
   * goldenLinesAPI.upload(formData, opts)
   * goldenLinesAPI.upload({ file, siteId, weekDate }, opts)
   */
  upload: (payload, opts = {}) => {
    let fd;

    if (payload && typeof payload.append === "function") {
      fd = payload;
    } else {
      const {
        file,
        siteId,
        weekDate,
        anyDateInWeek,
        debug = false,
      } = payload || {};

      if (!file) {
        throw new Error("No file selected");
      }

      fd = new FormData();
      fd.append("file", file);

      const sid = _glGetSiteId(siteId);

      if (sid) {
        fd.append("siteId", sid);
      }

      const selectedWeekDate = weekDate || anyDateInWeek;

      if (selectedWeekDate) {
        fd.append("weekDate", String(selectedWeekDate).slice(0, 10));
        fd.append("anyDateInWeek", String(selectedWeekDate).slice(0, 10));
      }

      if (debug) {
        fd.append("debug", "1");
      }
    }

    return api.post("/golden-lines/upload", fd, {
      timeout: opts.timeout ?? 15 * 60 * 1000,
      onUploadProgress: opts.onUploadProgress,
      params: cleanParams({
        ...(opts.params || {}),
        ...(opts.debug ? { debug: "1" } : {}),
      }),
    });
  },

uploadStatus: (jobId) =>
  api.get(`/golden-lines/upload-jobs/${sanitizeId(jobId)}`, {
    params: _withNoCache(),
  }),

  /*
   * Every flavour inside one product group, for this site.
   *
   * The manager route that answers this is director-only, so a site manager
   * asking about their own site needs their own way in. Same shape, same
   * service behind it.
   */
  siteGroupBreakdown: (params = {}) =>
    api.get("/golden-lines/site/group-breakdown", {
      params: withNoCache(cleanParams(params)),
    }),

  siteItems: (params = {}) => {
    const {
      siteId,
      department = "ALL",
      group = "none",
      groupKey = "",
      ...rest
    } = params;

    const weekParams = _glWeekParams(rest);

    return api.get("/golden-lines/site/items", {
      params: _withNoCache({
        siteId: _glGetSiteId(siteId),
        department,
        group,
        groupKey: String(groupKey || "").trim().toUpperCase(),
        ...weekParams,
      }),
    });
  },

  /*
   * Which weeks this site already has Golden Lines for.
   *
   * The page asks for a week and then reports, after the fact, that nothing
   * was imported for it — so finding a week with figures in it meant clicking
   * through them one at a time.
   */
  siteWeeks: (params = {}) =>
    api.get('/golden-lines/site/weeks', {
      params: _withNoCache({
        siteId: _glGetSiteId(params.siteId),
        ...(params.months ? { months: params.months } : {}),
      }),
    }),

  siteSummary: (params = {}) => {
    const {
      siteId,
      groupKey = "",
      ...rest
    } = params;

    const weekParams = _glWeekParams(rest);

    return api.get("/golden-lines/site/summary", {
      params: _withNoCache({
        siteId: _glGetSiteId(siteId),
        groupKey: String(groupKey || "").trim().toUpperCase(),
        ...weekParams,
      }),
    });
  },

  siteTop: (params = {}) => {
    const {
      siteId,
      sortBy = "sales",
      limit = 10,
      department = "",
      groupKey = "",
      ...rest
    } = params;

    const weekParams = _glWeekParams(rest);

    return api.get("/golden-lines/site/top", {
      params: _withNoCache({
        siteId: _glGetSiteId(siteId),
        sortBy,
        limit,
        ...(department ? { department } : {}),
        groupKey: String(groupKey || "").trim().toUpperCase(),
        ...weekParams,
      }),
    });
  },

  siteTopProducts: (params = {}) => goldenLinesAPI.siteTop(params),

  siteFamilies: (params = {}) => {
    const {
      siteId,
      department = "ALL",
      groupPacks = true,
      groupKey = "",
      ...rest
    } = params;

    const weekParams = _glWeekParams(rest);

    return api.get("/golden-lines/site/families", {
      params: _withNoCache({
        siteId: _glGetSiteId(siteId),
        department,
        groupPacks,
        groupKey: String(groupKey || "").trim().toUpperCase(),
        ...weekParams,
      }),
    });
  },

  directorSummary: (params = {}) => {
    const weekParams = _glWeekParams(params);

    return api.get("/golden-lines/director/summary", {
      params: _withNoCache({
        ...weekParams,
      }),
    });
  },

  directorTop: (params = {}) => {
    const {
      sortBy = "sales",
      limit = 10,
      department = "ALL",
      siteId = "",
      groupKey = "",
      ...rest
    } = params;

    const weekParams = _glWeekParams(rest);

    return api.get("/golden-lines/director/top", {
      params: _withNoCache({
        sortBy,
        limit,
        department,
        siteId: sanitizeId(siteId),
        groupKey: String(groupKey || "").trim().toUpperCase(),
        ...weekParams,
      }),
    });
  },

  directorSearch: (params = {}) => {
    const {
      q = "",
      sortBy = "sales",
      department = "ALL",
      siteId = "",
      groupKey = "",
      ...rest
    } = params;

    const weekParams = _glWeekParams(rest);

    return api.get("/golden-lines/director-search", {
      params: _withNoCache({
        q: String(q || "").trim(),
        sortBy,
        department,
        siteId: sanitizeId(siteId),
        groupKey: String(groupKey || "").trim().toUpperCase(),
        ...weekParams,
      }),
    });
  },

  uploadFamiliesTemplate: (file) => {
    if (!file) {
      throw new Error("No file selected");
    }

    const fd = new FormData();
    fd.append("file", file);

    return api.post("/golden-lines/families/upload", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  listFamilies: () =>
    api.get("/golden-lines/families", {
      params: _withNoCache(),
    }),

  uploadTPC: (file) => {
    if (!file) {
      throw new Error("No file selected");
    }

    const fd = new FormData();
    fd.append("file", file);

    return api.post("/golden-lines/tpc/upload", fd, {
      timeout: 10 * 60 * 1000,
    });
  },

  manualUpsert: (payload = {}) =>
    api.post("/golden-lines/manual", {
      ...payload,
      siteId: _glGetSiteId(payload.siteId),
    }),

  removeRecord: (id) =>
    api.delete(`/golden-lines/${sanitizeId(id)}`),
};

/* ================= GOLDEN LINES MANAGER ================= */

export const goldenLinesManagerAPI = {
  /* ---------- sites ---------- */

  getSites: () =>
    api.get("/gl-manager/sites", {
      params: _withNoCache(),
    }),

  /* ---------- departments ---------- */

  getDepartments: () =>
    api.get("/gl-manager/departments", {
      params: _withNoCache(),
    }),

  /* ---------- groups ---------- */

  getGroups: () =>
    api.get("/gl-manager/groups", {
      params: _withNoCache(),
    }),

  createGroup: (payload = {}) =>
    api.post("/gl-manager/groups", payload),

  updateGroup: (id, payload = {}) =>
    api.put(`/gl-manager/groups/${sanitizeId(id)}`, payload),

  deleteGroup: (id) =>
    api.delete(`/gl-manager/groups/${sanitizeId(id)}`),

  getGroupDetail: (id) =>
    api.get(`/gl-manager/groups/${sanitizeId(id)}/detail`, {
      params: _withNoCache(),
    }),

  getGroupProducts: (id, params = {}) =>
    api.get(`/gl-manager/groups/${sanitizeId(id)}/products`, {
      params: _withNoCache({
        ...params,
        ...(params.siteId ? { siteId: sanitizeId(params.siteId) } : {}),
        ...(params.weekStart
          ? { weekStart: String(params.weekStart).slice(0, 10) }
          : {}),
      }),
    }),

  /* ---------- SKUs / barcodes ---------- */

  addSkus: (groupId, barcodes = []) =>
    api.post(`/gl-manager/groups/${sanitizeId(groupId)}/skus`, {
      barcodes: Array.isArray(barcodes) ? barcodes : [barcodes],
    }),

  updateSku: (groupId, barcode, payload = {}) =>
    api.put(
      `/gl-manager/groups/${sanitizeId(groupId)}/skus/${encodeURIComponent(
        sanitizeId(barcode)
      )}`,
      payload
    ),

  activateSku: (groupId, barcode) =>
    api.put(
      `/gl-manager/groups/${sanitizeId(groupId)}/skus/${encodeURIComponent(
        sanitizeId(barcode)
      )}`,
      { isActive: true }
    ),

  deactivateSku: (groupId, barcode) =>
    api.put(
      `/gl-manager/groups/${sanitizeId(groupId)}/skus/${encodeURIComponent(
        sanitizeId(barcode)
      )}`,
      { isActive: false }
    ),

  /* ---------- upload audit / unmatched products ---------- */

  getAudit: (params = {}) =>
    api.get("/gl-manager/audit", {
      params: _withNoCache({
        ...params,
        ...(params.siteId ? { siteId: sanitizeId(params.siteId) } : {}),
        ...(params.weekStart
          ? { weekStart: String(params.weekStart).slice(0, 10) }
          : {}),
        ...(params.matchType ? { matchType: params.matchType } : {}),
        ...(params.q ? { q: params.q } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
      }),
    }),

  getUnmatchedAudit: (params = {}) =>
    api.get("/gl-manager/audit", {
      params: _withNoCache({
        ...params,
        matchType: "unmatched",
        ...(params.siteId ? { siteId: sanitizeId(params.siteId) } : {}),
        ...(params.weekStart
          ? { weekStart: String(params.weekStart).slice(0, 10) }
          : {}),
      }),
    }),

  assignAuditRow: (auditId, groupId) =>
    api.post(`/gl-manager/audit/${sanitizeId(auditId)}/assign`, {
      groupId: sanitizeId(groupId),
    }),

  bulkAssignAuditRows: (auditIds = [], groupId) =>
    api.post("/gl-manager/audit/bulk-assign", {
      auditIds: Array.isArray(auditIds) ? auditIds.map(sanitizeId) : [],
      groupId: sanitizeId(groupId),
    }),
};

export default api;
